import { lazy, Suspense, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Eye,
  Globe,
  Lock,
  MessageCircle,
  Search,
  ShieldCheck,
  Sparkles,
  Smartphone,
  Star,
  TrendingUp,
  UserPlus,
  Users,
  Video,
} from "lucide-react";
import Seo from "../../components/Seo/Seo";
import StructuredData from "../../components/Seo/StructuredData";
import {
  CANONICAL_BASE,
  DEFAULT_SITE_ALTERNATE_NAMES,
  DEFAULT_SITE_NAME,
} from "../../utils/seoDefaults";
import { homeV2Asset } from "../../utils/homeV2Assets";
import "../../styles/pmV2.css";
import "./HomePageV2.css";

const TRANSPARENT_PIXEL_SRC =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

let preSignupModalPromise = null;
const loadPreSignupModal = () => {
  if (!preSignupModalPromise) {
    preSignupModalPromise = import("../../components/PreSignupModal/PreSignupModal");
  }
  return preSignupModalPromise;
};
const PreSignupModal = lazy(loadPreSignupModal);

const HERO_IMAGE_WIDTHS = [320, 480, 640, 800];

const HERO_DESKTOP_MEDIA = "(min-width: 1081px)";
const HERO_MOBILE_MEDIA = "(max-width: 1080px)";

const HERO_DESKTOP_IMAGE_SIZES = "(min-width: 1081px) 260px, 1px";
const HERO_MOBILE_IMAGE_SIZES = "(max-width: 720px) 126px, (max-width: 1080px) 148px, 1px";
function buildHeroImageSrcSet(assetBase, format) {
  return HERO_IMAGE_WIDTHS.map(
    (width) => `${homeV2Asset(`${assetBase}-${width}.${format}`)} ${width}w`,
  ).join(", ");
}

const HOME_SEO_TITLE = "Acompanhantes virtuais online no Brasil | Private Muse";

const HOME_SEO_DESCRIPTION =
  "Encontre acompanhantes virtuais para sexo virtual por videochamada, chat privado e chamada de voz na Private Muse. Perfis verificados, atendimento discreto e 100% online.";

const HOME_STRUCTURED_DESCRIPTION =
  "A Private Muse conecta clientes a acompanhantes virtuais verificadas para sexo virtual por videochamada, chat privado e experiências 100% online.";

const HERO_PHOTOS = [
  {
    id: "isabella",
    name: "Isabella",
    age: 24,
    assetBase: "hero-isabella",
    src: homeV2Asset("hero-isabella.jpg"),
    width: 900,
    height: 648,
    city: "São Paulo",
    tag: "Verificada",
  },
  {
    id: "valentina",
    name: "Valentina",
    age: 26,
    assetBase: "hero-valentina",
    src: homeV2Asset("hero-valentina.jpg"),
    width: 900,
    height: 675,
    city: "Rio de Janeiro",
    tag: "Destaque",
  },
  {
    id: "sophia",
    name: "Sophia",
    age: 22,
    assetBase: "hero-sophia",
    src: homeV2Asset("hero-sophia.jpg"),
    width: 900,
    height: 1350,
    city: "Brasília",
    tag: "Online",
  },
  {
    id: "camila",
    name: "Camila",
    age: 25,
    assetBase: "hero-camila",
    src: homeV2Asset("hero-camila.jpg"),
    width: 900,
    height: 1350,
    city: "Curitiba",
    tag: "Nova",
  },
].map((photo) => ({
  ...photo,
  srcSets: {
    avif: buildHeroImageSrcSet(photo.assetBase, "avif"),
    webp: buildHeroImageSrcSet(photo.assetBase, "webp"),
    jpeg: `${photo.src} ${photo.width}w`,
  },
}));

const FEATURE_BADGES = [
  { icon: Video, label: "Videochamadas" },
  { icon: Smartphone, label: "WhatsApp & Telegram" },
  { icon: Globe, label: "100% Online" },
  { icon: ShieldCheck, label: "Discreto & Seguro" },
];

const VISITOR_STEPS = [
  {
    n: "1",
    title: "Escolher perfil",
    desc: "Navegue por perfis verificados e encontre uma acompanhante virtual alinhada ao seu momento.",
    mobileDesc: "Escolha um perfil verificado que combine com o que você procura.",
    icon: Search,
  },
  {
    n: "2",
    title: "Chamar no app",
    desc: "Abra o contato e combine a experiência diretamente pelo canal indicado no perfil.",
    mobileDesc: "Abra o contato indicado no perfil e chame pelo app.",
    icon: MessageCircle,
  },
  {
    n: "3",
    title: "Combinar online",
    desc: "Acerte detalhes de videochamada, chat, voz ou conteúdo com discrição.",
    mobileDesc: "Combine videochamada, chat ou voz diretamente online.",
    icon: Lock,
  },
];

const MODEL_STEPS = [
  {
    n: "1",
    title: "Criar conta",
    desc: "Cadastro gratuito para iniciar sua presença profissional na plataforma.",
    icon: UserPlus,
  },
  {
    n: "2",
    title: "Montar perfil",
    desc: "Adicione fotos, bio, formatos de atendimento e contatos reais.",
    icon: Star,
  },
  {
    n: "3",
    title: "Verificar identidade",
    desc: "Envie dados para confirmação de identidade e maioridade.",
    icon: ShieldCheck,
  },
  {
    n: "4",
    title: "Publicar perfil",
    desc: "Com o perfil aprovado, visitantes entram em contato com você.",
    icon: TrendingUp,
  },
];

const SEO_KEYWORDS = [
  "Acompanhantes virtuais",
  "Sexo virtual",
  "Videochamadas",
  "Chat privado",
  "Perfis verificados",
  "São Paulo",
  "Rio de Janeiro",
  "Belo Horizonte",
  "Curitiba",
  "100% online",
];

const FAQ_ITEMS = [
  {
    q: "O que é a Private Muse?",
    a: "Uma plataforma adulta premium para descobrir acompanhantes virtuais, perfis verificados e experiências online com mais privacidade e clareza.",
  },
  {
    q: "O cadastro é gratuito?",
    a: "Sim. Visitantes e acompanhantes podem iniciar o cadastro gratuitamente. Recursos de destaque podem existir como opções separadas dentro da plataforma.",
  },
  {
    q: "Como funciona a verificação?",
    a: "Acompanhantes passam por etapas de identidade e maioridade antes de concluir a publicação do perfil.",
  },
  {
    q: "A navegação é privada?",
    a: "A Private Muse trabalha com dados protegidos, fluxos discretos e informações organizadas conforme a LGPD.",
  },
];

function buildStructuredData() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${CANONICAL_BASE}/#website`,
        url: `${CANONICAL_BASE}/`,
        name: DEFAULT_SITE_NAME,
        alternateName: DEFAULT_SITE_ALTERNATE_NAMES,
        description: HOME_STRUCTURED_DESCRIPTION,
        inLanguage: "pt-BR",
      },
      {
        "@type": "Organization",
        "@id": `${CANONICAL_BASE}/#organization`,
        name: DEFAULT_SITE_NAME,
        alternateName: DEFAULT_SITE_ALTERNATE_NAMES,
        url: `${CANONICAL_BASE}/`,
        logo: `${CANONICAL_BASE}/logo512.png`,
      },
      {
        "@type": "WebPage",
        "@id": `${CANONICAL_BASE}/#webpage`,
        url: `${CANONICAL_BASE}/`,
        name: HOME_SEO_TITLE,
        description: HOME_SEO_DESCRIPTION,
        inLanguage: "pt-BR",
        isPartOf: { "@id": `${CANONICAL_BASE}/#website` },
        about: { "@id": `${CANONICAL_BASE}/#organization` },
      },
    ],
  };
}

const HOME_STRUCTURED_DATA = buildStructuredData();

function ProfileImage({
  photo,
  alt,
  loading,
  fetchPriority,
  media,
  sizes,
  shouldLoad = true,
}) {
  const sourceMediaProps = media ? { media } : {};
  const imageSrc = shouldLoad && !media ? photo.src : TRANSPARENT_PIXEL_SRC;
  const priorityProps = fetchPriority ? { fetchpriority: fetchPriority } : {};

  return (
    <picture>
      {shouldLoad && (
        <>
          <source
            {...sourceMediaProps}
            type="image/avif"
            srcSet={photo.srcSets.avif}
            sizes={sizes}
          />
          <source
            {...sourceMediaProps}
            type="image/webp"
            srcSet={photo.srcSets.webp}
            sizes={sizes}
          />
          <source
            {...sourceMediaProps}
            type="image/jpeg"
            srcSet={photo.srcSets.jpeg}
            sizes={sizes}
          />
        </>
      )}
      <img
        src={imageSrc}
        alt={alt}
        width={photo.width}
        height={photo.height}
        loading={loading}
        decoding="async"
        sizes={sizes}
        {...priorityProps}
      />
    </picture>
  );
}

function ProfileCard({
  photo,
  compact = false,
  imageMedia,
  imageSizes = HERO_MOBILE_IMAGE_SIZES,
  imageLoading = "eager",
  imageFetchPriority,
  imageShouldLoad = true,
}) {
  return (
    <Link
      className={`home-v2-profile-card${compact ? " home-v2-profile-card--compact" : ""}`}
      to="/acompanhantes-virtuais/"
    >
      <ProfileImage
        photo={photo}
        alt={`Perfil de ${photo.name}`}
        loading={imageLoading}
        fetchPriority={imageFetchPriority}
        media={imageMedia}
        sizes={imageSizes}
        shouldLoad={imageShouldLoad}
      />
      <span className="home-v2-profile-card__overlay" aria-hidden="true" />
      <span className="home-v2-profile-card__meta">
        <span className="home-v2-profile-card__name">
          <span>{photo.name}</span>
          <small>{photo.age}</small>
        </span>
        {!compact && (
          <span className="home-v2-profile-card__row home-v2-profile-card__row--tag-only">
            <strong>{photo.tag}</strong>
          </span>
        )}
      </span>
    </Link>
  );
}

export default function HomePage() {
  const [howItWorksTab, setHowItWorksTab] = useState("visitor");
  const [isPreSignupOpen, setIsPreSignupOpen] = useState(false);
  const currentSteps = howItWorksTab === "visitor" ? VISITOR_STEPS : MODEL_STEPS;
  const openSignup = () => {
    loadPreSignupModal();
    setIsPreSignupOpen(true);
  };
  const closeSignup = () => setIsPreSignupOpen(false);

  return (
    <>
    <div className="pm-v2-scope home-v2-page">
      <StructuredData id="home-structured-data" data={HOME_STRUCTURED_DATA} />
      <Seo title={HOME_SEO_TITLE} description={HOME_SEO_DESCRIPTION} />

      <section className="home-v2-hero" aria-labelledby="home-v2-hero-title">
        <div className="home-v2-hero__ambient" aria-hidden="true" />
        <div className="home-v2-hero__noise" aria-hidden="true" />

        <div className="pm-v2-container home-v2-hero__inner">
          <div className="home-v2-hero__copy">
            <div className="home-v2-hero__title-group">
              <h1 id="home-v2-hero-title" className="pm-v2-heading home-v2-hero__title">
                <span className="home-v2-hero__title-line">Acompanhantes virtuais</span>
              </h1>
              <p className="home-v2-hero__subtitle">
                Perfis Verificados · 100% Online
              </p>
            </div>

            <div className="home-v2-mobile-carousel" aria-label="Perfis em destaque">
              {HERO_PHOTOS.slice(0, 3).map((photo) => (
                <ProfileCard
                  key={`mobile-${photo.id}`}
                  photo={photo}
                  compact
                  imageMedia={HERO_MOBILE_MEDIA}
                  imageSizes={HERO_MOBILE_IMAGE_SIZES}
                  imageLoading="eager"
                />
              ))}
            </div>

            <div className="home-v2-badges" aria-label="Destaques da plataforma">
              {FEATURE_BADGES.map(({ icon: Icon, label }) => (
                <span key={label} className="home-v2-badge">
                  <Icon size={14} aria-hidden="true" />
                  {label}
                </span>
              ))}
            </div>

            <div className="home-v2-hero__actions">
              <Link
                to="/acompanhantes-virtuais/"
                className="pm-v2-button pm-v2-button--primary hero-cta-button"
              >
                <Search size={17} aria-hidden="true" />
                Ver acompanhantes
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
              <Link
                to="/cadastro-acompanhante/"
                className="pm-v2-button pm-v2-button--secondary"
              >
                <TrendingUp size={16} aria-hidden="true" />
                Quero anunciar
              </Link>
            </div>

            <button
              type="button"
              className="home-v2-hero__free-access"
              onPointerEnter={loadPreSignupModal}
              onFocus={loadPreSignupModal}
              onClick={openSignup}
              aria-label="Criar conta gratuita"
            >
              <div>
                <span>
                  <Sparkles size={12} aria-hidden="true" />
                  Acesso gratuito
                </span>
                <strong>Crie sua conta e aproveite</strong>
              </div>
              <span className="home-v2-hero__free-access-arrow" aria-hidden="true">
                <ArrowRight size={17} aria-hidden="true" />
              </span>
            </button>

            <p className="pm-v2-copy home-v2-hero__lead">
              {"Encontre acompanhantes virtuais reais para "}
              <strong>videochamadas</strong>
              {", "}
              <strong>sexo virtual</strong>
              {", chat privado e interações 100% online via "}
              <strong>WhatsApp ou Telegram</strong>
              {". Uma experiência premium, discreta e totalmente segura."}
            </p>

            <Link className="home-v2-login-link" to="/login">
              <ArrowRight size={14} aria-hidden="true" />
              Já tenho conta - entrar
            </Link>
          </div>

          <div className="home-v2-hero__gallery" aria-label="Perfis em destaque">
            {HERO_PHOTOS.map((photo, index) => (
              <ProfileCard
                key={photo.id}
                photo={photo}
                imageMedia={HERO_DESKTOP_MEDIA}
                imageSizes={HERO_DESKTOP_IMAGE_SIZES}
                imageLoading="eager"
                imageFetchPriority={index === 0 ? "high" : undefined}
              />
            ))}
          </div>

          <ChevronDown className="home-v2-scroll-hint" size={18} aria-hidden="true" />
        </div>
      </section>

      <section className="pm-v2-section home-v2-how" aria-labelledby="home-v2-how-title">
        <div className="pm-v2-container">
          <div className="home-v2-section-heading">
            <p className="pm-v2-eyebrow">Simples e direto</p>
            <h2 id="home-v2-how-title" className="pm-v2-heading pm-v2-heading--sm">
              Como funciona
            </h2>
          </div>

          <div className="home-v2-tabs" role="tablist" aria-label="Fluxo de uso">
            <button
              type="button"
              role="tab"
              aria-selected={howItWorksTab === "visitor"}
              className={howItWorksTab === "visitor" ? "is-active" : ""}
              onClick={() => setHowItWorksTab("visitor")}
            >
              <Eye size={15} aria-hidden="true" />
              Para você
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={howItWorksTab === "model"}
              className={howItWorksTab === "model" ? "is-active" : ""}
              onClick={() => setHowItWorksTab("model")}
            >
              <Users size={15} aria-hidden="true" />
              Acompanhantes
            </button>
          </div>

          <div
            className={
              howItWorksTab === "model"
                ? "home-v2-steps home-v2-steps--model"
                : "home-v2-steps"
            }
          >
            {currentSteps.map(({ n, title, desc, mobileDesc, icon: Icon }) => (
              <article key={`${howItWorksTab}-${n}`} className="pm-v2-card home-v2-step-card">
                <div className="home-v2-step-card__top">
                  <span className="home-v2-step-card__icon">
                    <Icon size={17} aria-hidden="true" />
                  </span>
                  <h3>{title}</h3>
                  <span className="home-v2-step-card__number">{n}</span>
                </div>
                <p
                  className={
                    mobileDesc
                      ? "home-v2-step-card__desc home-v2-step-card__desc--desktop"
                      : "home-v2-step-card__desc"
                  }
                >
                  {desc}
                </p>
                {mobileDesc && (
                  <p className="home-v2-step-card__desc home-v2-step-card__desc--mobile">
                    {mobileDesc}
                  </p>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pm-v2-section home-v2-faq" aria-labelledby="home-v2-faq-title">
        <div className="pm-v2-container home-v2-faq__inner">
          <div className="home-v2-section-heading">
            <p className="pm-v2-eyebrow">Dúvidas</p>
            <h2 id="home-v2-faq-title" className="pm-v2-heading pm-v2-heading--sm">
              Perguntas frequentes
            </h2>
          </div>

          <div className="home-v2-faq__list">
            {FAQ_ITEMS.map(({ q, a }) => (
              <details key={q} className="pm-v2-card home-v2-faq-item">
                <summary>
                  {q}
                  <ArrowRight size={16} aria-hidden="true" />
                </summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="pm-v2-section home-v2-seo" aria-labelledby="home-v2-seo-title">
        <div className="pm-v2-container home-v2-seo__inner">
          <div className="home-v2-keywords" aria-label="Temas principais">
            {SEO_KEYWORDS.map((keyword) => (
              <span key={keyword}>{keyword}</span>
            ))}
          </div>

          <h2 id="home-v2-seo-title" className="pm-v2-heading pm-v2-heading--sm">
            Private Muse - acompanhantes virtuais em todo o Brasil
          </h2>

          <div className="home-v2-seo__copy">
            <p>
              {"A Private Muse é uma plataforma premium de "}
              <strong>acompanhantes virtuais</strong>
              {" para quem busca experiências online com discrição. Aqui você encontra perfis verificados para "}
              <strong>sexo virtual</strong>
              {", videochamada, chat privado, chamada de voz e conteúdos digitais."}
            </p>
            <p>
              Por ser uma jornada 100% online, a busca não depende de uma cidade
              específica. Você pode comparar perfis de São Paulo, Rio de Janeiro,
              Belo Horizonte, Curitiba, Salvador, Fortaleza, Porto Alegre, Brasília e
              outras regiões antes de seguir para o contato indicado.
            </p>
            <p>
              A home organiza as principais rotas da plataforma para visitantes e
              acompanhantes: catálogo de perfis, cadastro gratuito, orientações de uso,
              privacidade, verificação de maioridade e links internos para formatos de
              atendimento virtual.
            </p>
          </div>
        </div>
      </section>

      <section className="pm-v2-section home-v2-final-cta" aria-labelledby="home-v2-final-title">
        <div className="pm-v2-container">
          <div className="pm-v2-card home-v2-final-cta__card">
            <span className="pm-v2-pill">
              <CheckCircle2 size={13} aria-hidden="true" />
              100% gratuito
            </span>
            <h2 id="home-v2-final-title" className="pm-v2-heading pm-v2-heading--sm">
              Entre para a Private Muse
            </h2>
            <p className="pm-v2-copy">
              Cadastro gratuito para anunciantes e visitantes.
              Privacidade, elegância e autonomia.
            </p>
            <div className="home-v2-final-cta__actions">
              <Link className="pm-v2-button pm-v2-button--primary" to="/cadastro-acompanhante/">
                <TrendingUp size={16} aria-hidden="true" />
                Quero Anunciar - Grátis
              </Link>
              <Link className="pm-v2-button pm-v2-button--secondary" to="/cadastro/?tab=cliente">
                <Eye size={16} aria-hidden="true" />
                Ver Acompanhantes
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
      {isPreSignupOpen && (
        <Suspense fallback={null}>
          <PreSignupModal isOpen={isPreSignupOpen} onClose={closeSignup} />
        </Suspense>
      )}
    </>
  );
}
